import cronstrue from "cronstrue";
import produce from "immer";
import { useEffect, useRef, useState } from "react";
import {
  Controller,
  FormProvider,
  useController,
  useForm,
  useFormContext,
  useFormState,
} from "react-hook-form";

import {
  IdentifierCasing,
  OrchestrationSchedulerType,
  useDataWarehouseSchemasQuery,
  useIntegrationConfigurationQuery,
} from "@/apollo/types";
import { UtilityButton } from "@/components/elements/Button";
import Toggle from "@/components/elements/Toggle";
import Center from "@/components/elements/layout/Center";
import LoadingSpinner from "@/components/elements/LoadingSpinner";
import { CronDisplayReadable } from "@/components/elements/CronDisplayReadable";
import { CronInput, TimeInput } from "@/components/elements/SyncScheduler";
import Tooltip from "@/components/elements/Tooltip";
import { SchedulerSelecter } from "@/components/modules/SchedulerSelecter";
import {
  SyncScheduleRadioGroup,
  WeldSyncScheduler,
} from "@/components/modules/WeldSyncScheduler";
import { SyncSchedulerMenu } from "@/components/modules/WeldSyncScheduler/components/SyncSchedulerMenu";
import { InputDescription } from "@/components/primitives/InputDescription";
import { InputError } from "@/components/primitives/InputError";
import { Input } from "@/components/primitives/input";
import {
  applyIdentifierCasing,
  IDENTIFIER_CASING_DESCRIPTION,
  IDENTIFIER_CASING_LABEL,
} from "@/features/data-warehouse-schemas";
import {
  OrchestrationFeatureGuard,
  useOrchestrationFeatureGuard,
} from "@/features/feature-guards";
import classNames from "@/helpers/classNames";
import { useMountEffect } from "@/hooks/useMountEffect";
import { useMixpanel } from "@/monitoring/mixpanel";
import { useToast } from "@/providers/ToastProvider";
import { useDataWarehouse } from "@/providers/account";
import mergeRefs from "@/utils/mergeRefs";
import { InfoIcon } from "lucide-react";

import { useStepsContext } from "../../../stepper/useSteps";
import { Content, Footer, Heading, SubHeading } from "../../components/Layout";
import { NextButton, PrevButton } from "../../components/NavButton";
import {
  useCreateSync,
  useDataSourceStepContext,
} from "../../contexts/DataSourceStepsContext";
import { useDataSourceStateContext } from "../../step-state";
import { AdditionalDestinationsField } from "./AdditionalDestinationsField";

type FormModel = ReturnType<
  typeof useDataSourceStateContext
>["state"]["syncSettings"]["value"];

function generateSchemaName(input: string) {
  return input.trim().replace(/\W/g, "_").replace(/__+/g, "_");
}

/**
 * The warehouse's casing has to be known before the form initialises: react-hook-form reads
 * `defaultValues` once, so a name generated before the query lands would keep the wrong casing.
 */
export function SyncSettingsStepContainer() {
  const { data, loading, error } = useDataWarehouseSchemasQuery();

  if (loading) {
    return (
      <Center className="h-full">
        <LoadingSpinner />
      </Center>
    );
  }

  // An account that predates DataWarehouseInfo resolves to no record, and a failed query tells us
  // nothing about the casing — both mean the backend will leave the name as typed.
  return (
    <SyncSettingsForm
      warehouseIdentifierCasing={
        error ? undefined : data?.dataWarehouseSchemas?.defaultIdentifierCasing
      }
    />
  );
}

function SyncSettingsForm({
  warehouseIdentifierCasing,
}: {
  warehouseIdentifierCasing: IdentifierCasing | null | undefined;
}) {
  const { onGoToStep } = useStepsContext();
  const { state, setState } = useDataSourceStateContext();
  const { connection, closeOnCreate, onCancelSetup } =
    useDataSourceStepContext();
  const mixpanel = useMixpanel();

  const toast = useToast();

  const identifierCasing =
    state.syncSettings.value.identifierCasing ??
    warehouseIdentifierCasing ??
    null;

  const uncasedSchemaName =
    state.syncSettings.value.destinationSchemaName ||
    generateSchemaName(connection?.label || connection?.integrationId || "");

  // The name as the user last authored it. Casing the field in place would otherwise destroy their
  // capitalisation the first time the switch goes off.
  const [authoredSchemaName, setAuthoredSchemaName] =
    useState(uncasedSchemaName);

  const formMethods = useForm<FormModel>({
    mode: "onChange",
    defaultValues: {
      ...state.syncSettings.value,
      identifierCasing,
      destinationSchemaName: applyIdentifierCasing(
        uncasedSchemaName,
        identifierCasing,
      ),
    },
  });

  const { isValid, isDirty } = useFormState({
    control: formMethods.control,
  });

  useEffect(() => {
    setState(
      produce((state) => {
        state.syncSettings.isDirty = isDirty;
      }),
    );
  }, [isDirty, setState]);

  const schedulerType = formMethods.watch("schedulerType");

  const { isEnabled: isOrchestrationEnabled } = useOrchestrationFeatureGuard();
  const { setValue } = formMethods;
  useEffect(() => {
    if (
      isOrchestrationEnabled === false &&
      schedulerType === OrchestrationSchedulerType.Global
    ) {
      setValue("schedulerType", OrchestrationSchedulerType.Local);
    }
  }, [schedulerType, isOrchestrationEnabled, setValue]);

  const [createSync, { loading }] = useCreateSync({
    onCompleted: () => {
      if (closeOnCreate && connection?.integrationId !== "webhook") {
        mixpanel.track("Data Source Setup Completed Step Viewed", {
          integration_id: connection?.integrationId,
          auto_closed: true,
        });
        onCancelSetup({ forceClose: true });
        return;
      }
      onGoToStep("setup-complete");
    },
    onError: (error) => {
      toast(`Sync not created`, error.message, "error");
    },
  });

  return (
    <FormProvider {...formMethods}>
      <form
        onSubmit={formMethods.handleSubmit((data) => {
          const newState = produce(state, (draft) => {
            draft.syncSettings.isValid = true;
            draft.syncSettings.value = data;
          });
          setState(newState);
          createSync(newState);
        })}
        className="flex h-full w-full flex-col"
      >
        <Content>
          <Heading>Configure sync</Heading>
          <div className="flex flex-col gap-4">
            <OrchestrationFeatureGuard>
              <Scheduler />
            </OrchestrationFeatureGuard>
            {schedulerType === OrchestrationSchedulerType.Local && (
              <LocalSchedule />
            )}
          </div>
          <hr className="my-8 dark:border-gray-700" />
          <DestinationSchema onAuthorName={setAuthoredSchemaName} />
          <IdentifierCasingField authoredSchemaName={authoredSchemaName} />
          <AdditionalDestinationsField
            streams={[...state.tables.value.sourceStreams.values()]}
            sourceIntegrationId={connection?.integrationId ?? ""}
          />
        </Content>
        <Footer className="flex-row-reverse">
          <NextButton
            isDisabled={!isValid}
            isLoading={loading}
            loadingText="Starting ..."
          >
            Start Initial Sync
          </NextButton>
          <PrevButton />
        </Footer>
      </form>
    </FormProvider>
  );
}

function Scheduler() {
  const formContext = useFormContext<FormModel>();

  const schedulerTypeCtrl = useController({
    name: "schedulerType",
    control: formContext.control,
    rules: {
      required: {
        value: true,
        message: "Please select a schedule",
      },
    },
  });

  const orchestrationWorkflowIdCtrl = useController({
    name: "orchestrationWorkflowId",
    control: formContext.control,
    rules: {
      validate: (value) => {
        if (
          formContext.getValues("schedulerType") ===
            OrchestrationSchedulerType.Global &&
          !value
        ) {
          return "Please select a workflow";
        }
        return true;
      },
    },
  });

  return (
    <SchedulerSelecter
      header={<SubHeading className="mb-2">Schedule</SubHeading>}
      hideLabel
      configuration={{
        allowNewWorkflow: false,
        allowNoSchedule: false,
      }}
      orchestrationScheduler={schedulerTypeCtrl.field.value}
      orchestrationWorkflowId={orchestrationWorkflowIdCtrl.field.value}
      onUpdate={(config) => {
        if (!config.orchestrationScheduler) return;
        schedulerTypeCtrl.field.onChange(config.orchestrationScheduler);
        orchestrationWorkflowIdCtrl.field.onChange(
          config.orchestrationWorkflowId,
        );
      }}
    />
  );
}

function LocalSchedule() {
  const formContext = useFormContext<FormModel>();
  return (
    <Controller
      name="scheduleKey"
      rules={{
        validate: {
          required: (value) => {
            if (
              formContext.getValues("schedulerType") ===
              OrchestrationSchedulerType.Local
            ) {
              return !!value || "Please select a schedule";
            }
            return true;
          },
          validCron: (value) => {
            try {
              cronstrue.toString(value, { verbose: true });
              return true;
            } catch (_e) {
              return "Invalid cron expression";
            }
          },
        },
      }}
      render={({ field }) => (
        <WeldSyncScheduler cron={field.value} onChange={field.onChange}>
          {({ value, mode, showTimeOffsetInput }) => (
            <>
              <div>
                <SubHeading className="mb-2">
                  How often do you want to sync data with Weld?
                </SubHeading>
                <CronInput />
                <SyncScheduleRadioGroup className="grid grid-cols-2 gap-x-4 gap-y-3" />
                {showTimeOffsetInput && (
                  <>
                    <SubHeading className="mt-4 mb-2">Starting at</SubHeading>
                    <TimeInput />
                  </>
                )}
              </div>
              <div>
                <p className="text-sm font-medium">
                  Will sync now and update data:
                </p>
                <div className="flex items-center gap-4 text-sm">
                  <span>
                    {value && (
                      <CronDisplayReadable verbose cron={value.value} />
                    )}
                    {!value && mode === "custom" && (
                      <span>
                        Write a cron expression to see update frequency here
                      </span>
                    )}
                  </span>
                  <SyncSchedulerMenu />
                </div>
              </div>
            </>
          )}
        </WeldSyncScheduler>
      )}
    />
  );
}

/**
 * Always sends an explicit value rather than leaving it unset: an omitted casing inherits the
 * warehouse default, which would silently undo turning the switch off.
 */
function IdentifierCasingField({
  authoredSchemaName,
}: {
  authoredSchemaName: string;
}) {
  const dw = useDataWarehouse();
  const formContext = useFormContext<FormModel>();
  const identifierCasing = formContext.watch("identifierCasing");
  const { data: integration } = useIntegrationConfigurationQuery({
    variables: { id: dw.integrationId ?? "", isDatawarehouse: true },
    skip: !dw.integrationId,
  });

  if (!integration?.integrationConfiguration.supportsIdentifierCasing) {
    return null;
  }

  const isUppercase = identifierCasing === IdentifierCasing.Uppercase;

  return (
    <div className="mt-6">
      <label className="flex items-center gap-3 text-sm">
        <Toggle
          checked={isUppercase}
          onChange={(checked) => {
            const casing = checked ? IdentifierCasing.Uppercase : null;
            // shouldDirty, or toggling alone leaves the slideover thinking nothing changed and it
            // closes without the discard confirmation.
            formContext.setValue("identifierCasing", casing, {
              shouldDirty: true,
            });
            // Re-cased from what the user authored rather than from the field, so switching off
            // gives their own capitalisation back instead of lower-casing it.
            formContext.setValue(
              "destinationSchemaName",
              applyIdentifierCasing(authoredSchemaName, casing),
              { shouldValidate: true, shouldDirty: true },
            );
          }}
        />
        <span>{IDENTIFIER_CASING_LABEL}</span>
      </label>
      <InputDescription className="mt-2">
        {IDENTIFIER_CASING_DESCRIPTION}
      </InputDescription>
    </div>
  );
}

function DestinationSchema({
  onAuthorName,
}: {
  onAuthorName: (name: string) => void;
}) {
  const formContext = useFormContext<FormModel>();
  const identifierCasing = formContext.watch("identifierCasing");
  const { ref, onChange, ...rest } = formContext.register(
    "destinationSchemaName",
    {
      required: {
        value: true,
        message: "Destination schema name is required",
      },
      validate: {
        onlyWordCharacters: (value) => {
          return (
            !/\W/g.test(value) ||
            "Only letters, numbers and underscores are allowed"
          );
        },
        noDoubleUnderscores: (value) => {
          return (
            !/__/g.test(value) || 'Cannot contain double underscores ("__")'
          );
        },
      },
    },
  );

  const { errors } = useFormState({ control: formContext.control });
  const errorsForField = errors["destinationSchemaName"];

  const inputRef = useRef<HTMLInputElement>(null);
  const [isEditable, setIsEditable] = useState(false);

  useEffect(() => {
    if (isEditable) {
      inputRef.current?.focus();
    }
  }, [isEditable]);

  useMountEffect(() => {
    // Trigger validation on mount since the input will be in readonly mode initially
    formContext.trigger("destinationSchemaName");
  });

  return (
    <div>
      <SubHeading className="mb-2 flex items-center space-x-1">
        <span>Destination schema name</span>
        <Tooltip
          content={
            "Name of the directory in the data warehouse, where synced tables are stored in."
          }
        >
          <div>
            <InfoIcon className="size-4" />
          </div>
        </Tooltip>
      </SubHeading>
      <div className="relative w-1/2 pr-2">
        <Input
          ref={mergeRefs(ref, inputRef)}
          className={classNames("w-full", !isEditable && "pr-12")}
          placeholder="Name the dataset"
          readOnly={!isEditable}
          onChange={(event) => {
            onAuthorName(event.target.value);
            // The backend cases whatever it is given, so casing it here keeps the field showing
            // the schema that will actually be created rather than what was typed.
            event.target.value = applyIdentifierCasing(
              event.target.value,
              identifierCasing,
            );
            return onChange(event);
          }}
          {...rest}
        />
        {!isEditable && (
          <div className="absolute top-0 right-0 flex h-12 items-center pr-6">
            <UtilityButton
              className=""
              onClick={() => {
                setIsEditable(true);
              }}
            >
              Edit
            </UtilityButton>
          </div>
        )}
      </div>
      {errorsForField && (
        <InputError className="mt-1">
          {errorsForField.message?.toString()}
        </InputError>
      )}
    </div>
  );
}
